import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { hasPlanFeature, type PlanFeatureKey } from '../../types/planTiers';

interface RequirePlanFeatureProps {
  /** The page renders when the program's plan includes ANY of these. */
  anyOf: readonly PlanFeatureKey[];
  children: ReactNode;
  /** Where to send a user whose plan has none of them. The dashboard by default. */
  redirectTo?: string;
}

/**
 * Route guard for a page a plan tier can leave out, the plan counterpart of `RequireFeature`. A
 * program whose plan lacks the feature reads the page as absent, the same as the API answering 404.
 *
 * Fails open: `planFeatures` absent or null (an API older than plan tiers, or no plan tier) means
 * every feature. Renders nothing while auth is still initializing, so a deep link is not bounced
 * before the profile has arrived.
 */
export const RequirePlanFeature = ({
  anyOf,
  children,
  redirectTo = '/players',
}: RequirePlanFeatureProps) => {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const planFeatures = user?.profile?.planFeatures;
  if (!anyOf.some((key) => hasPlanFeature(planFeatures, key))) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
};
